import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Plug, ShieldAlert, XCircle } from 'lucide-react';
import toast from '../../design-system/toast';

import { Spinner } from '../../design-system';
import { useOAuthConsent } from '../../hooks/useOAuthConsent';
import { ApiKeyScope } from '../../types/apiKey';
import { API_KEY_SCOPE_OPTIONS } from '../../utils/apiKeyScopes';

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-ui-background-primary flex items-center justify-center p-4">
    <div className="bg-ui-background-secondary border border-ui-border-primary rounded-xl shadow-sm w-full max-w-md p-6 space-y-4">
      {children}
    </div>
  </div>
);

const Message: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon,
  title,
  children,
}) => (
  <Card>
    <div className="flex items-center gap-2">
      {icon}
      <h1 className="text-lg font-semibold text-ui-text-primary">{title}</h1>
    </div>
    <p className="text-sm text-ui-text-secondary">{children}</p>
  </Card>
);

// Every tool needs read access, so it is always granted.
const REQUIRED_SCOPE: ApiKeyScope = 'read';

/**
 * Consent screen for remote MCP clients connecting via OAuth.
 *
 * `/oauth/authorize` on the API validates the client's request and redirects here with
 * `?request=<id>`. The signed-in user picks an organization and the scopes their role
 * allows; approving sends the browser back to the client with an authorization code.
 */
const OAuthConsentPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const { consent, isLoading, error, errorMessage, approve, deny } = useOAuthConsent(requestId);

  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [unchecked, setUnchecked] = useState<Set<ApiKeyScope>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [leaving, setLeaving] = useState<'approved' | 'denied' | null>(null);

  const organizations = useMemo(() => consent?.organizations ?? [], [consent]);
  const selectedOrg =
    organizations.find((org) => org.organization_id === selectedOrgId) ?? organizations[0] ?? null;
  const allowedScopes = selectedOrg?.allowed_scopes ?? [];
  const chosenScopes = allowedScopes.filter((scope) => scope === REQUIRED_SCOPE || !unchecked.has(scope));
  const withheldScopes = (consent?.requested_scopes ?? []).filter((scope) => !allowedScopes.includes(scope));

  const toggleScope = (scope: ApiKeyScope) => {
    setUnchecked((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) next.delete(scope);
      else next.add(scope);
      return next;
    });
  };

  const leaveTo = (url: string, outcome: 'approved' | 'denied') => {
    setLeaving(outcome);
    window.location.assign(url);
  };

  const handleApprove = async () => {
    if (!selectedOrg || chosenScopes.length === 0) return;
    setSubmitting(true);
    try {
      leaveTo(await approve(selectedOrg.organization_id, chosenScopes), 'approved');
    } catch (err: any) {
      const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to approve';
      toast.error(typeof message === 'string' ? message : 'Failed to approve');
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setSubmitting(true);
    try {
      leaveTo(await deny(), 'denied');
    } catch (err: any) {
      const message = err?.data?.detail?.detail || err?.data?.detail || err?.message || 'Failed to deny';
      toast.error(typeof message === 'string' ? message : 'Failed to deny');
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-6">
          <Spinner size={32} />
          <p className="text-sm text-ui-text-tertiary">Loading connection request…</p>
        </div>
      </Card>
    );
  }

  if (leaving) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Spinner size={32} />
          <p className="text-sm text-ui-text-secondary">
            {leaving === 'approved' ? 'Connected. ' : ''}Returning you to {consent?.client_name ?? 'the app'}…
          </p>
        </div>
      </Card>
    );
  }

  if (error === 'expired') {
    return (
      <Message icon={<AlertCircle className="w-5 h-5 text-amber-500" />} title="Connection request expired">
        This request has expired or was already answered. Start the connection again from the app you are
        connecting.
      </Message>
    );
  }

  if (error === 'generic') {
    return (
      <Message icon={<AlertCircle className="w-5 h-5 text-red-500" />} title="Something went wrong">
        {errorMessage}
      </Message>
    );
  }

  if (error === 'not_found' || !consent) {
    return (
      <Message icon={<AlertCircle className="w-5 h-5 text-red-500" />} title="Connection request not found">
        This link is invalid. Start the connection again from the app you are connecting.
      </Message>
    );
  }

  const noOrgs = organizations.length === 0;

  return (
    <Card>
      <div className="flex items-center gap-2">
        <Plug className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode" />
        <h1 className="text-lg font-semibold text-ui-text-primary">Connect {consent.client_name}</h1>
      </div>

      <p className="text-sm text-ui-text-secondary">
        <strong className="text-ui-text-primary">{consent.client_name}</strong> wants to access your Kolega
        organization on your behalf. After you approve you'll be sent back to{' '}
        <span className="font-mono text-ui-text-primary">{consent.redirect_host}</span>.
      </p>

      <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
        <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Only approve if you just started this connection yourself. If you don't recognize{' '}
          <span className="font-mono">{consent.redirect_host}</span>, deny it.
        </p>
      </div>

      {noOrgs ? (
        <p className="text-sm text-ui-text-secondary">
          You aren't a member of any organization that can be connected.
        </p>
      ) : (
        <>
          <div>
            <label htmlFor="oauth-org-select" className="block text-sm font-medium text-ui-text-secondary mb-1">
              Organization
            </label>
            <select
              id="oauth-org-select"
              value={selectedOrg?.organization_id ?? ''}
              onChange={(e) => {
                setSelectedOrgId(e.target.value);
                setUnchecked(new Set());
              }}
              disabled={submitting}
              className="w-full px-4 py-2 border border-ui-border-primary rounded-lg
                         bg-ui-background-secondary text-ui-text-primary
                         focus:ring-2 focus:ring-brand-primary focus:border-transparent
                         disabled:opacity-50"
            >
              {organizations.map((org) => (
                <option key={org.organization_id} value={org.organization_id}>
                  {org.organization_name}
                </option>
              ))}
            </select>
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-ui-text-secondary mb-2">It will be able to</legend>
            <div className="space-y-2">
              {API_KEY_SCOPE_OPTIONS.filter((option) => allowedScopes.includes(option.value)).map((option) => {
                const required = option.value === REQUIRED_SCOPE;
                return (
                  <label key={option.value} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={required || !unchecked.has(option.value)}
                      disabled={required || submitting}
                      onChange={() => toggleScope(option.value)}
                    />
                    <span>
                      <span className="block text-sm text-ui-text-primary">{option.label}</span>
                      <span className="block text-xs text-ui-text-tertiary">{option.description}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {withheldScopes.length > 0 && (
              <p className="mt-2 text-xs text-ui-text-tertiary">
                Your role in this organization is read-only, so write access can't be granted.
              </p>
            )}
          </fieldset>
        </>
      )}

      <p className="text-xs text-ui-text-tertiary">
        You can disconnect it any time in Organization Settings → Connected apps.
      </p>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={handleDeny}
          disabled={submitting}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700
                     disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2"
        >
          {submitting ? <Spinner size={16} tone="current" /> : <XCircle className="w-4 h-4" />}
          Deny
        </button>
        <button
          type="button"
          onClick={handleApprove}
          disabled={submitting || noOrgs || chosenScopes.length === 0}
          className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark
                     disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
        >
          {submitting ? <Spinner size={16} tone="current" /> : <CheckCircle className="w-4 h-4" />}
          Approve
        </button>
      </div>
    </Card>
  );
};

export default OAuthConsentPage;
